import round from 'lodash/round';

import {
  Button,
  Card,
  Icons,
  Skeleton,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import { useDoesReportingMonetizationApply } from '@cast/shared-trial-ui';

import { NumberFormat } from 'components/number/NumberFormat';
import { Link } from 'components/router';
import { usePlatformUsageReportQuery } from 'hooks/queries/organization';

import { DATABASE_OPTIMIZATION_FEATURE_KEY } from './database-optimization/constants';
import { getUnitLabel, sortPlatformUsageFeatures } from './_utils';
import { WidgetContainer } from '../../_components/WidgetContainer';

const Widget = () => {
  const { data, isLoading } = usePlatformUsageReportQuery();

  const features = sortPlatformUsageFeatures(data?.features ?? []);

  if (isLoading) {
    return (
      <Card sx={{ padding: 0 }}>
        {Array.from({ length: 3 }, (_, i) => (
          <Stack key={i} padding={16} gap={4}>
            <Skeleton variant="text" width={120} height={12} />
            <Skeleton variant="text" width={80} height={36} />
          </Stack>
        ))}
      </Card>
    );
  }

  return (
    <Card sx={{ padding: 0 }} data-testid="platform-usage-widget">
      {features.map((feature) => {
        if (!feature.feature) return null;
        return (
          <Stack key={feature.feature} padding={16} gap={4}>
            <Typography
              variant="L10R"
              color={tokens.theme.fg.structural.primary}
              textTransform="uppercase"
            >
              {feature.displayName ?? feature.feature}
            </Typography>
            <Stack direction="row" alignItems="baseline" gap={8}>
              <Typography
                color={tokens.theme.fg.structural.primary}
                variant="h3"
              >
                <NumberFormat
                  thousandSeparator
                  displayType="text"
                  value={round(
                    feature.usage ?? 0,
                    feature.feature === DATABASE_OPTIMIZATION_FEATURE_KEY
                      ? 2
                      : 1
                  )}
                />
              </Typography>
              {getUnitLabel(feature.unit) && (
                <Typography
                  variant="L10R"
                  color={tokens.theme.fg.structural.secondary}
                  textTransform="uppercase"
                >
                  {getUnitLabel(feature.unit)}
                </Typography>
              )}
            </Stack>
          </Stack>
        );
      })}
    </Card>
  );
};

export const PlatformUsageWidget = () => {
  const hideClassicBillingCards = useDoesReportingMonetizationApply();
  if (hideClassicBillingCards) {
    return null;
  }

  return (
    <WidgetContainer
      heading={
        <Stack direction="row" justifyContent="space-between" gap={16}>
          <Typography variant="A1" color={tokens.theme.fg.structural.secondary}>
            Platform usage this month
          </Typography>
          <Button
            variant="ghost"
            size="sm"
            endIcon={<Icons.ArrowRight />}
            component={Link}
            to="../billing-report"
          >
            View report
          </Button>
        </Stack>
      }
      gap="4px"
    >
      <Widget />
    </WidgetContainer>
  );
};
